import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Package, Plane, Tv } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleTravelClick = () => {
    navigate('/travel');
  };

  const handleShippingClick = () => {
    navigate('/shipping');
  };

  const handleEntertainmentClick = () => {
    navigate('/entertainment');
  };

  return (
    <div className="min-h-screen bg-[#09090B] relative overflow-hidden">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 px-4 md:px-8 py-4 md:py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="ZN Networks" className="h-10 md:h-12 w-auto" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>
                Networks
              </h1>
              <p className="text-white/60 text-xs hidden md:block" style={{ fontFamily: 'Manrope' }}>Bridging Worlds. Moving Futures.</p>
            </div>
          </div>
          <div className="flex gap-2 md:gap-4">
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-primary text-sm md:text-base px-4 md:px-8 py-2 md:py-4"
                data-testid="dashboard-btn"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="border border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-3 md:px-6 py-2 md:py-3 uppercase tracking-widest text-xs font-bold transition-all duration-300"
                  data-testid="login-btn"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="btn-primary text-sm md:text-base px-4 md:px-8 py-2 md:py-4"
                  data-testid="register-btn"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Three Column Layout - Desktop */}
      <div className="hidden md:flex h-screen">
        {/* Travel Section */}
        <div
          className="split-screen-hover flex-1 relative cursor-pointer group overflow-hidden"
          onClick={handleTravelClick}
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1692796225783-2923440dff19)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="travel-section"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-black/60 group-hover:from-blue-800/70 group-hover:to-black/50 transition-all duration-500"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-12 text-center">
            <div className="bg-[#FFD60A]/10 backdrop-blur-sm border border-[#FFD60A]/30 p-6 mb-6 group-hover:scale-110 transition-transform duration-500">
              <Plane className="w-16 h-16 text-[#FFD60A]" strokeWidth={1.5} />
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-4 text-white" style={{ fontFamily: 'Sora' }}>TRAVEL</h2>
            <p className="text-lg text-white/80 max-w-md mb-8" style={{ fontFamily: 'Manrope' }}>
              Book flights, hotels, and visa assistance
            </p>
            <div className="btn-primary group-hover:scale-105 transition-transform duration-300">
              Explore Travel
            </div>
          </div>
        </div>

        {/* Shipping Section */}
        <div
          className="split-screen-hover flex-1 relative cursor-pointer group overflow-hidden"
          onClick={handleShippingClick}
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1729572060488-14da8e9827cc)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="shipping-section"
        >
          <div className="absolute inset-0 bg-gradient-to-bl from-red-900/80 to-black/60 group-hover:from-red-800/70 group-hover:to-black/50 transition-all duration-500"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-12 text-center">
            <div className="bg-[#FF3B30]/10 backdrop-blur-sm border border-[#FF3B30]/30 p-6 mb-6 group-hover:scale-110 transition-transform duration-500">
              <Package className="w-16 h-16 text-[#FF3B30]" strokeWidth={1.5} />
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-4 text-white" style={{ fontFamily: 'Sora' }}>SHIPPING</h2>
            <p className="text-lg text-white/80 max-w-md mb-8" style={{ fontFamily: 'Manrope' }}>
              Fast shipping USA ↔ Nigeria with camera scan
            </p>
            <div className="btn-secondary group-hover:scale-105 transition-transform duration-300">
              Get Quote
            </div>
          </div>
        </div>

        {/* Entertainment Section */}
        <div
          className="split-screen-hover flex-1 relative cursor-pointer group overflow-hidden"
          onClick={handleEntertainmentClick}
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574267432644-f610cab75e7e)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="entertainment-section"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/80 to-black/60 group-hover:from-purple-800/70 group-hover:to-black/50 transition-all duration-500"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-12 text-center">
            <div className="bg-purple-500/10 backdrop-blur-sm border border-purple-500/30 p-6 mb-6 group-hover:scale-110 transition-transform duration-500">
              <Tv className="w-16 h-16 text-purple-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-4 text-white" style={{ fontFamily: 'Sora' }}>ENTERTAINMENT</h2>
            <p className="text-lg text-white/80 max-w-md mb-8" style={{ fontFamily: 'Manrope' }}>
              Free streaming channels & premium content
            </p>
            <div className="bg-purple-600 text-white hover:bg-purple-700 font-semibold px-6 py-4 transition-all duration-300 group-hover:scale-105">
              Watch Now
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Stacked Cards */}
      <div className="md:hidden min-h-screen pt-24 pb-20 px-4">
        {/* Travel Card */}
        <div
          className="mb-6 relative cursor-pointer overflow-hidden h-64 card-glass"
          onClick={handleTravelClick}
          data-testid="travel-mobile"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-transparent"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
            <Plane className="w-12 h-12 text-[#FFD60A] mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Sora' }}>TRAVEL</h2>
            <p className="text-sm text-white/80 mb-4" style={{ fontFamily: 'Manrope' }}>Flights & Hotels</p>
            <div className="btn-primary text-sm px-6 py-3">Explore</div>
          </div>
        </div>

        {/* Shipping Card */}
        <div
          className="mb-6 relative cursor-pointer overflow-hidden h-64 card-glass"
          onClick={handleShippingClick}
          data-testid="shipping-mobile"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/30 to-transparent"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
            <Package className="w-12 h-12 text-[#FF3B30] mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Sora' }}>SHIPPING</h2>
            <p className="text-sm text-white/80 mb-4" style={{ fontFamily: 'Manrope' }}>Camera Scan Available</p>
            <div className="btn-secondary text-sm px-6 py-3">Get Quote</div>
          </div>
        </div>

        {/* Entertainment Card */}
        <div
          className="mb-6 relative cursor-pointer overflow-hidden h-64 card-glass"
          onClick={handleEntertainmentClick}
          data-testid="entertainment-mobile"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 to-transparent"></div>
          <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
            <Tv className="w-12 h-12 text-purple-400 mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Sora' }}>ENTERTAINMENT</h2>
            <p className="text-sm text-white/80 mb-4" style={{ fontFamily: 'Manrope' }}>Free Streaming</p>
            <div className="bg-purple-600 text-white font-semibold px-6 py-3 text-sm">Watch Now</div>
          </div>
        </div>
      </div>

      {/* Bottom CTAs */}
      <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center gap-2 md:gap-4 px-4">
        <button
          onClick={() => navigate('/track')}
          className="border border-white/20 text-white hover:bg-white/10 backdrop-blur-md px-4 md:px-8 py-2 md:py-4 text-xs md:text-sm uppercase tracking-widest font-bold transition-all duration-300"
          data-testid="track-shipment-btn"
        >
          Track
        </button>
        <button
          onClick={() => navigate('/contact')}
          className="border border-white/20 text-white hover:bg-white/10 backdrop-blur-md px-4 md:px-8 py-2 md:py-4 text-xs md:text-sm uppercase tracking-widest font-bold transition-all duration-300"
          data-testid="contact-btn"
        >
          Contact
        </button>
      </div>
    </div>
  );
};

export default Landing;